import { useAtom } from 'jotai';
import { type KeyboardEvent, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useObjects, useSearchRecords } from '../../data/hooks';
import { cx, Dialog, Input, Popover, SearchField, Spinner, useIsDesktop } from '../../ui';
import { commandMenuOpenAtom } from '../session';

/**
 * THE GLOBAL SEARCH IS ATTACHED TO ITS FIELD (wave 4 round 1, C2-4; owner-69: "Search
 * oeffnet so ein Fenster und so weit ich weiss, macht Apple das nicht").
 *
 * It was a centred palette - `ui/Dialog` with the title hidden - which is a construction
 * from another platform: Apple leaves the field where it is and hangs the suggestions
 * underneath it, in place, which is what Mail and Finder draw and what the HIG describes
 * for a search field's suggestions
 * (hig: https://developer.apple.com/design/human-interface-guidelines/search-fields).
 *
 * SO THE DESKTOP RENDERS A POPOVER UNDER THE SIDEBAR'S FIELD and the phone keeps a sheet,
 * because on a phone Apple's own global search IS a screen rather than a panel hanging off
 * a 44px row. `SearchPopover` is the desktop half and lives where its anchor lives (the
 * sidebar); `CommandMenu` is the phone half and stays mounted by the shell. Both read the
 * same atom, so cmd-k opens whichever one this width has.
 *
 * IT IS A LISTBOX, NOT A DIALOG. A panel a person never moves into is the field's
 * suggestions list; Radix names its content `role="dialog"`, and `ui/Popover` lets the
 * caller say otherwise for exactly this case.
 *
 * A STUB in the sense that it has two sources - the object list and record search - and no
 * command registry yet; the interaction, the keyboard model and the search wiring are real.
 */
interface Entry {
  readonly key: string;
  readonly label: string;
  readonly hint: string;
  readonly to: string;
}

/** cmd-k / ctrl-k anywhere. Registered once, on window, so no screen has to know about it. */
function useCommandKey(open: (value: boolean) => void): void {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent<Element> | globalThis.KeyboardEvent) => {
      if (event.key.toLowerCase() === 'k' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        open(true);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open]);
}

/**
 * The two sources, the cursor and the keyboard model - one copy for both breakpoints.
 *
 * `dismiss` and `dismissedThisPress` are the half that owns the ESCAPE STAIRCASE: the
 * desktop popover hands both in and gets the three steps below, the phone sheet hands
 * neither in and keeps Radix's own dismiss, because a sheet a person moved into is closed
 * by the layer it is.
 */
function useSearchModel(
  open: boolean,
  dismiss?: (next: boolean) => void,
  dismissedThisPress?: () => boolean,
) {
  const [term, setTerm] = useState('');
  const [cursor, setCursor] = useState(0);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const objects = useObjects();
  const search = useSearchRecords(term);

  useEffect(() => {
    if (open) {
      setTerm('');
      setCursor(0);
    }
  }, [open]);

  /**
   * A SCREEN CHANGE EMPTIES THE FIELD (wave 5, A2-1; triage SH-W5-4, where the term stood
   * in the sidebar's field on Home and again on Agencies). The field is the window's, not
   * the screen's, and what it held was a QUERY - Mail's field is the same control and its
   * suggestions belong to the search a person is running, not to the window
   * (support: https://support.apple.com/guide/mail/search-for-emails-mlhlp1003/mac). The
   * suggestions go with the term: a panel hanging under the field over a screen the person
   * has already left is the search still running after its answer was picked.
   */
  const screen = useRef(pathname);
  useEffect(() => {
    if (screen.current === pathname) return;
    screen.current = pathname;
    setTerm('');
    setCursor(0);
    dismiss?.(false);
  }, [pathname, dismiss]);

  const needle = term.trim().toLowerCase();
  const objectEntries: readonly Entry[] = (objects.data ?? [])
    .filter((object) => needle.length === 0 || object.labelPlural.toLowerCase().includes(needle))
    .map((object) => ({
      key: `object-${object.id}`,
      label: object.labelPlural,
      hint: 'Open list',
      to: `/objects/${object.id}`,
    }));
  const recordEntries: readonly Entry[] = (search.data ?? []).map((hit) => ({
    key: `record-${hit.recordId}`,
    label: hit.label,
    hint: hit.secondary ?? 'Record',
    to: `/objects/${hit.objectId}/records/${hit.recordId}`,
  }));
  const entries = [...objectEntries, ...recordEntries];
  const active = entries[Math.min(cursor, entries.length - 1)];

  const go = (entry: Entry | undefined) => {
    if (entry === undefined) return;
    void navigate(entry.to);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setCursor((current) => Math.min(current + 1, Math.max(entries.length - 1, 0)));
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setCursor((current) => Math.max(current - 1, 0));
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      go(active);
    }
    /**
     * ESCAPE IS ONE STEP PER PRESS (wave 5, A2-1; triage SH-W5-4, where one press closed
     * the panel and left both the term and the focus where they were, for ever).
     *
     * Cocoa binds the key to a single cancel message - doc:
     * https://developer.apple.com/library/archive/documentation/Cocoa/Conceptual/EventOverview/HandlingKeyEvents/HandlingKeyEvents.html
     * "For example, to handle the default binding of the Escape key, the responder would
     * override the `cancelOperation:` method of `NSResponder`." - and the staircase a
     * search field walks is the one Apple's own fields walk, web:
     * https://macmost.com/13-uses-for-the-mac-esc-key.html "Esc clears a search field in
     * System Settings, and in Safari's address bar it dismisses suggestions, then clears
     * text, then exits the field."
     *
     * SO: the suggestions go, then the term goes, then the field goes. The default is
     * prevented at every step because a `type="search"` input clears itself in the browser
     * otherwise - which is how the second press used to clear the term AND reopen the
     * panel, since the clear arrives as an `input` event the field answers by opening.
     */
    if (event.key === 'Escape' && dismissedThisPress !== undefined) {
      event.preventDefault();
      /* STEP ONE IS THE LAYER'S OWN, AND IT HAS ALREADY HAPPENED. Radix listens for Escape
         on the document in the capture phase, so by the time this handler runs the panel is
         gone and `open` already reads false - which is how a first press used to fall
         through to step two and clear the term as well. The popover says whether THIS press
         was the one it dismissed on; if it was, the press is spent. */
      if (dismissedThisPress()) return;
      if (term !== '') {
        setTerm('');
        setCursor(0);
        return;
      }
      event.currentTarget.blur();
    }
  };

  return {
    term,
    setTerm,
    cursor,
    setCursor,
    entries,
    objectCount: objectEntries.length,
    active,
    go,
    onKeyDown,
    search,
  };
}

type SearchModel = ReturnType<typeof useSearchModel>;

function SearchResults({
  model,
  onPick,
}: {
  readonly model: SearchModel;
  readonly onPick: () => void;
}) {
  /**
   * THE SUGGESTIONS ARE GROUPED, BECAUSE THAT IS WHAT A SUGGESTIONS LIST IS (wave 5, A2-2;
   * triage SH-W5-5). support:
   * https://support.apple.com/guide/mail/search-for-emails-mlhlp1003/mac - "As you type,
   * suggestions appear below the search field. They are organized into categories, such as
   * Subject or Attachments, depending on your search text." The two categories this field
   * has are the two sources it reads, and the heading is the sidebar's own header register
   * (`.shell-nav-heading`, the kit's `Sidebars/{Light,Dark}/Medium/Header`), because this
   * panel hangs off the sidebar's field and a second heading style beside it would be a
   * third way of writing the same thing. A group with nothing in it draws no heading.
   */
  const groups = [
    { heading: 'Objects', from: 0, to: model.objectCount },
    { heading: 'Records', from: model.objectCount, to: model.entries.length },
  ] as const;
  return (
    <>
      <div className="max-h-80 overflow-y-auto">
        {groups.map((group) =>
          group.to === group.from ? null : (
            <section key={group.heading}>
              <p className="shell-nav-heading">{group.heading}</p>
              <ul className="space-y-0.5">
                {model.entries.slice(group.from, group.to).map((entry, offset) => {
                  const index = group.from + offset;
                  return (
                    <li key={entry.key}>
                      <button
                        type="button"
                        onClick={() => {
                          model.go(entry);
                          onPick();
                        }}
                        onMouseEnter={() => model.setCursor(index)}
                        className={cx(
                          'flex min-h-(--touch-target-min) w-full items-center justify-between gap-3 rounded-control px-3 text-left text-sm',
                          index === model.cursor ? 'bg-surface-hover text-ink' : 'text-ink-muted',
                        )}
                      >
                        {/* THE NAME IS THE ROW, SO THE HINT GIVES WAY FIRST. A record's name
                            is what a person is looking for and the category beside it is the
                            answer to "which kind"; when the two cannot both be whole, the
                            shorter one yields. */}
                        <span className="min-w-0 truncate">{entry.label}</span>
                        <span className="min-w-0 shrink-[9999] truncate text-2xs text-ink-subtle">
                          {entry.hint}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          ),
        )}
      </div>
      {model.search.isFetching && (
        <p className="mt-3 flex items-center gap-2 px-3 text-xs text-ink-subtle">
          <Spinner /> Searching
        </p>
      )}
      {!model.search.isFetching && model.entries.length === 0 && (
        <p className="mt-3 px-3 py-6 text-center text-sm text-ink-subtle">
          Nothing matches "{model.term}".
        </p>
      )}
      {/*
        AND NO KEYBOARD FOOTER (wave 4 round 2, item 0c). Mail's and Finder's suggestion
        popovers carry no instruction row, and the copy deck says the same thing as a rule:
        UI text is a label, a hint or an error, never a set of directions (R10, and the
        cross-cutting line on jargon). The keys still work; they are simply not written on
        the panel, which is also what made the panel taller than its results.
      */}
    </>
  );
}

/**
 * The name is the same in both halves: one control, one thing to look for. "Search", the decided
 * word (redesign register O4; brief FE-SIDEBAR section 1: "Search on top with the placeholder
 * "Search"").
 */
const SEARCH_LABEL = 'Search';

/**
 * THE DESKTOP HALF, rendered by the sidebar so it hangs under the sidebar's own field. The
 * field is the trigger AND keeps focus: `keepFocus` stops the panel taking it, which is
 * what lets a person go on typing into the thing they clicked.
 */
export function SearchPopover() {
  const [open, setOpen] = useAtom(commandMenuOpenAtom);
  const desktop = useIsDesktop();
  /**
   * WHICH PRESS THE LAYER ATE. Radix's dismiss runs on the document in the CAPTURE phase,
   * before any handler inside the tree, so by the time the field's own handler runs the
   * panel is already gone and `open` already reads false: the field cannot ask whether the
   * panel WAS open, it can only be told. The flag is set when the panel closes and cleared
   * on the next turn of the event loop - not in a microtask, because the browser drains
   * those between two listeners of the SAME event and the flag would be gone before the
   * field ever read it (measured on this build, 2026-09-16).
   */
  const dismissed = useRef(false);
  const onOpenChange = (next: boolean) => {
    if (!next) {
      dismissed.current = true;
      setTimeout(() => {
        dismissed.current = false;
      }, 0);
    }
    setOpen(next);
  };
  const model = useSearchModel(open, setOpen, () => dismissed.current);
  useCommandKey(setOpen);
  const field = (
    <SearchField
      context="sidebar"
      label={SEARCH_LABEL}
      value={model.term}
      onChange={(event) => {
        model.setTerm(event.target.value);
        model.setCursor(0);
        if (!open) setOpen(true);
      }}
      onKeyDown={model.onKeyDown}
    />
  );
  if (!desktop) return field;
  return (
    /**
     * THE PANEL HANGS FROM THE FIELD'S OWN BOX, so the trigger is a box AROUND the field
     * rather than the field itself: a trigger merges its props onto what it wraps, and
     * merged onto the input the panel would have measured from the input's left edge -
     * eleven pixels and a magnifier inside the capsule the person is looking at.
     *
     * AND IT IS WIDER THAN THE FIELD (wave 5, A2-2; triage SH-W5-5, where "Larkwood Studio"
     * ended in an ellipsis beside its own category). `matchTrigger` tied the panel to the
     * sidebar field's 228px, which is the width of a PLACE to type rather than the width of
     * an answer; Apple's own picture of this control shows the suggestions standing wider
     * than the field with the names whole (support:
     * https://support.apple.com/guide/mail/search-for-emails-mlhlp1003/mac, picture
     * https://help.apple.com/assets/6940590395B73B67500DF914/69405904627DAC39E4013406/en_US/ab740e20d4741d78f424cf9cb39c1ada.png).
     * Apple publishes no number for it, so the panel takes the primitive's own `default`
     * column (20rem) rather than a number of this round's: 20rem clears the kit's sidebar
     * column (kit: Apple macOS 27 UI Kit.sketch / Windows/Light/Unified Toolbar + Title +
     * Sidebar/Active, Sidebar 256) and is the smallest of the primitive's two widths that
     * leaves a record's name and its category on one line. `align="start"` keeps the panel
     * starting where the field starts.
     */
    <Popover
      trigger={<div className="shell-search-anchor">{field}</div>}
      open={open}
      onOpenChange={onOpenChange}
      align="start"
      side="bottom"
      role="listbox"
      ariaLabel="Search results"
      keepFocus
    >
      <div data-shell-search-panel>
        <SearchResults model={model} onPick={() => setOpen(false)} />
      </div>
    </Popover>
  );
}

/**
 * THE PHONE HALF. Below the breakpoint the sidebar is not on the screen, so there is no
 * field to hang a panel from and the search is its own surface - which is what iOS does
 * with a global search anyway. This is the construction the palette always was, kept for
 * the one width where it is right.
 */
export function CommandMenu() {
  const [open, setOpen] = useAtom(commandMenuOpenAtom);
  const desktop = useIsDesktop();
  const model = useSearchModel(open);
  useCommandKey(setOpen);
  if (desktop) return null;
  return (
    <Dialog open={open} onOpenChange={setOpen} title="Search" hideTitle width="md">
      <div>
        {/* The phone's palette keeps the CONTENT field it always had - a text control with
            the magnifier in it - rather than the search shell: this panel is a surface of
            its own, not a field with a list under it, and `type="search"` would announce a
            searchbox where the screen reader has always heard the search's name. */}
        <Input
          context="content"
          icon="search"
          value={model.term}
          onChange={(event) => {
            model.setTerm(event.target.value);
            model.setCursor(0);
          }}
          onKeyDown={model.onKeyDown}
          placeholder={SEARCH_LABEL}
          aria-label={SEARCH_LABEL}
        />
        <div className="mt-3">
          <SearchResults model={model} onPick={() => setOpen(false)} />
        </div>
      </div>
    </Dialog>
  );
}
